import CartSummary from "../templates/components/cartSummary.handlebars";

export class Cart {
    constructor(rootElement, salesData, applyDiscountListener, removeDiscountListener) {
        this.rootElement = rootElement;
        this.applyDiscountListener = applyDiscountListener;
        this.removeDiscountListener = removeDiscountListener;
        this.eventId = salesData.eventId;
        this.feePercent = (salesData.feePercent/100);
        this.tickets = {}; // tickets[shuttleId] = { qty: 2, price: 40, subtotal: 80, shuttle: {} }
        this.passes = {
            qty: 0,
            name: salesData.multidayPassName,
            price: salesData.multidayPassPrice,
            subtotal: 0,
            shuttles: []
        };
        this.shuttleData = {}; // shuttleData[id] = { location: "", date: "", time: "", price: 0 }

        for (const schedule of salesData.schedules) {
            for (const station of schedule.stations) {
                for (const shuttle of station.shuttles) {
                    this.shuttleData[shuttle.id] = {
                        id: shuttle.id,
                        location: station.name,
                        date: shuttle.date,
                        time: shuttle.name,
                        price: shuttle.price,
                    }
                }
            }
        }

        this.render();
    }

    setDiscount(discount, message=null) {
        this.discount = discount;
        this.discountMessage = message;
        this.render();
    }

    getPromoCodeInputValue() {
        return this.promoCodeInput.value;
    }

    addPass(shuttleIds) {
        const shuttles = shuttleIds.map((shuttleId) => this.shuttleData[shuttleId]);
        this.passes.qty++;
        this.passes.subtotal = this.passes.qty * this.passes.price;
        this.passes.shuttles = shuttles;
        this.render();
    }

    updatePassConfiguration(shuttleIds) {
        const shuttles = shuttleIds.map((shuttleId) => this.shuttleData[shuttleId]);
        this.passes.shuttles = shuttles;
        this.render();
    }

    getPasses() {
        return this.passes;
    }

    getLineItems() {
        const getDetails = (shuttle) => {
            return [
                `Date: ${shuttle.date}`,
                `Location: ${shuttle.location}`,
                `Time: ${shuttle.time}`,
            ];
        }

        const lineitem = (ticket, type) => { // type = ticket || pass
            const singleDay = (type === "ticket");
            const name = singleDay ? `Round Trip Ticket` : `Multiday Round Trip Ticket`;
            const qty = ticket.qty;
            const price = ticket.price;
            const discount = this.getDiscountAmount(ticket.price);
            const discountedPrice = (price - discount);
            const subtotal = (qty * discountedPrice);
            const total = (qty * price);
            const details = [];

            if (singleDay) {
                details.push(...getDetails(ticket.shuttle));
            } else {
                for (const shuttle of ticket.shuttles) {
                    details.push(...getDetails(shuttle));
                    details.push("<hr>");
                }
            }
            details.push(`<b>Price: $${price.toFixed(2)} per ${type}</b>`);

            return {
                name: name,
                quantity: qty,
                price: price.toFixed(2),
                hasDiscount: (discount > 0),
                discount: discount.toFixed(2),
                discountedPrice: discountedPrice.toFixed(2),
                total: total.toFixed(2),
                subtotal: subtotal.toFixed(2),
                details: details
            }
        }

        const lineItems = [];
        if (this.passes.qty > 0) {
            lineItems.push(lineitem(this.passes, "pass"));
        }

        for (const shuttleId of Object.keys(this.tickets)) {
            const ticket = this.tickets[shuttleId];
            lineItems.push(lineitem(ticket, "ticket"));
        }
        return lineItems;
    }

    removePass() {
        if (this.passes.qty <= 0) return;
        this.passes.qty--;
        this.render();
    }

    addTicket(shuttleId) {
        const shuttle = this.shuttleData[shuttleId];
        if (!shuttle) return;

        if (shuttleId in this.tickets) {
            const ticket = this.tickets[shuttleId];
            ticket.qty++;
            ticket.subtotal = ticket.qty * ticket.price;
        }
        else {
            this.tickets[shuttleId] = { qty: 1, price: shuttle.price, subtotal: shuttle.price, shuttle: shuttle };
        }
        this.render();
    }

    getTickets() {
        return this.tickets;
    }

    getTicket(shuttleId) {
        return this.tickets[shuttleId];
    }

    removeTicket(shuttleId) {
        if (shuttleId in this.tickets) {
            this.tickets[shuttleId].qty--;
            if (this.tickets[shuttleId].qty <= 0) {
                delete this.tickets[shuttleId];
            }
            this.render();
        }
    }

    getTicketSubtotal() {
        const subtotal = Object.keys(this.tickets).reduce((total, currentKey) => {
            const price = this.shuttleData[currentKey].price;
            const qty = this.tickets[currentKey].qty;
            const lineItemPrice = (price * qty);
            return total + lineItemPrice;
        }, 0);
        return subtotal;
    }

    getPassSubtotal() {
        if (this.passes.qty == 0) return 0;
        const subtotal = this.passes.qty * this.passes.price;
        return subtotal;
    }

    getSubtotal() {
        return this.getTicketSubtotal() + this.getPassSubtotal();
    }

    getDiscountAmount(price) {
        if (this.discount != null) {
            if (this.discount.discountPercent > 0) {
                return (price * (this.discount.discountPercent/100));
            } else if (this.discount.discountAmount > 0) {
                return this.discount.discountAmount;
            }
        }
        return 0;
    }

    getDiscount() {
        if (this.discount != null) {
            if (this.discount.discountPercent > 0) {
                return (this.getSubtotal() * (this.discount.discountPercent/100));
            } else if (this.discount.discountAmount > 0) {
                return this.discount.discountAmount;
            }
        }
        return 0;
    }

    getPromoCode() {
        return this.discount?.discountCode || null;
    }

    getFees() {
        const price = (this.getSubtotal() - this.getDiscount());
        return (price * this.feePercent);
    }

    getTotal() {
        const total = this.getSubtotal() - this.getDiscount() + this.getFees();
        return total;
    }

    render() {
        const data = {
            discount: this.discount,
            discountMessage: this.discountMessage,
            lineItems: this.getLineItems(),
            subtotal: (this.getSubtotal() - this.getDiscount()).toFixed(2),
            fees: this.getFees().toFixed(2),
            total: this.getTotal().toFixed(2)
        };

        this.addCodeListener = (e) => {
            if (e.type === "keydown" && e.key !== "Enter") {
                return;
            }
            const applyButton = this.rootElement.querySelector("#apply-code");
            if (applyButton) {
                applyButton.classList.add("button-loading");
                applyButton.disabled = true;
            }
            this.promoCode = this.getPromoCodeInputValue();
            this.applyDiscountListener(this.promoCode);
        }

        this.removeCodeListener = (e) => {
            this.removeDiscountListener(this.promoCode);
        }

        let addButton = this.rootElement.querySelector("#apply-code");
        let removeButton = this.rootElement.querySelector("#remove-code");
        let codeInput = this.rootElement.querySelector("#promo-code");

        addButton?.removeEventListener("click", this.addCodeListener);
        codeInput?.removeEventListener("keydown", this.addCodeListener);
        removeButton?.removeEventListener("click", this.removeCodeListener);

        this.rootElement.innerHTML = CartSummary(data);
        this.promoCodeInput = this.rootElement.querySelector("#promo-code");

        addButton = this.rootElement.querySelector("#apply-code");
        removeButton = this.rootElement.querySelector("#remove-code");
        codeInput = this.rootElement.querySelector("#promo-code");

        addButton?.addEventListener("click", this.addCodeListener);
        codeInput?.addEventListener("keydown", this.addCodeListener);
        removeButton?.addEventListener("click", this.removeCodeListener);
    }
}
